// ── ADMIN: BILLING (outstanding balances + payment history reports) ───
// esc (shared classic script) stays a bare global — not migrated yet.
// viewParent (core/routing.js) is a cross-cutting nav primitive, only ever
// reached via onclick="" in the tables below, deliberately left that way —
// see onclick-guardrail memory. cardModalForUser (billing-shared.js) and
// chargeCardConfirm (admin-dashboard.js) are real imports; their buttons
// live inside #obBody, which gets rebuilt both by route()'s normal
// VIEW_INIT lifecycle AND by updateReports() (called directly on filter
// change, bypassing that lifecycle) — wireCardButtons() re-attaches after
// both, since #obBody's rendered HTML changes either way. exportHistory and
// the phFrom/phTo/phStatus filter fields live in the static VIEWS['a-reports']
// template (never rewritten by updateReports(), which only touches
// #obBody/#phBody), so they're wired once from VIEW_INIT alongside
// wireCardButtons().
import { DB } from '../core/state.js';
import { TODAY, fmtD } from '../core/format.js';
import { acctName, childName } from '../core/domain-utils.js';
import { exportCSV } from '../core/ui.js';
import { VIEWS, VIEW_INIT } from '../core/routing.js';
import { cardModalForUser } from './billing-shared.js';
import { chargeCardConfirm } from './admin-dashboard.js';

VIEWS['a-reports'] = () => {
  const y   = TODAY.getFullYear();
  const pad = n => String(n).padStart(2, '0');
  const todayStr = `${y}-${pad(TODAY.getMonth() + 1)}-${pad(TODAY.getDate())}`;
  const fromStr  = `${y}-01-01`;
  return `
    <div class="page-head">
      <h2 class="serif">Billing</h2>
    </div>
    <div class="card">
      <div class="card-hd">Outstanding balances</div>
      <div id="obBody">${outstandingTable()}</div>
    </div>
    <div class="card">
      <div class="card-hd">Payment history <button class="btn-ghost btn-sm" id="exportHistoryBtn">Export CSV</button></div>
      <div class="filters">
        <div class="field"><label>From</label><input type="date" id="phFrom" value="${fromStr}"></div>
        <div class="field"><label>To</label><input type="date" id="phTo" value="${todayStr}"></div>
        <div class="field"><label>Status</label><select id="phStatus">
          <option value="">All</option><option>paid</option><option>due</option><option>refunded</option>
        </select></div>
      </div>
      <div id="phBody">${historyTable()}</div>
    </div>`;
};

function updateReports() {
  document.getElementById('obBody').innerHTML = outstandingTable();
  document.getElementById('phBody').innerHTML = historyTable();
  wireCardButtons();
}
function wireCardButtons() {
  document.querySelectorAll('#obBody [data-action="card-modal-for-user"]').forEach(btn => {
    btn.addEventListener('click', () => cardModalForUser(btn.dataset.account));
  });
  document.querySelectorAll('#obBody [data-action="charge-card-confirm"]').forEach(btn => {
    btn.addEventListener('click', () => chargeCardConfirm(btn.dataset.account, btn.dataset.name, Number(btn.dataset.total)));
  });
}
VIEW_INIT['a-reports'] = () => {
  wireCardButtons();
  const exportBtn = document.getElementById('exportHistoryBtn');
  if (exportBtn) exportBtn.addEventListener('click', exportHistory);
  const fromInp = document.getElementById('phFrom');
  if (fromInp) fromInp.addEventListener('change', updateReports);
  const toInp = document.getElementById('phTo');
  if (toInp) toInp.addEventListener('change', updateReports);
  const statusSel = document.getElementById('phStatus');
  if (statusSel) statusSel.addEventListener('change', updateReports);
};

const getVal = (id, fallback) => { const el = document.getElementById(id); return el ? el.value : fallback; };


function agingBucket(date) {
  const days = Math.floor((TODAY - new Date(date + 'T00:00')) / 86400000);
  return days >= 90 ? '90+ days' : days >= 60 ? '60 days' : days >= 30 ? '30 days' : 'Current';
}
function outstandingRows() {
  const byAcct = {};
  DB.charges.filter(c => c.status === 'due').forEach(c => {
    (byAcct[c.accountId] = byAcct[c.accountId] || []).push(c);
  });
  return Object.entries(byAcct).map(([acct, rows]) => {
    const u = DB.users.find(u => String(u.id) === String(acct));
    return {
      acctId: acct,
      account: acctName(acct),
      hasCard: !!(u && u.card),
      total: rows.reduce((s,c)=>s+c.amount,0),
      oldest: rows.reduce((m,c) => c.date < m ? c.date : m, rows[0].date),
      bucket: agingBucket(rows.reduce((m,c) => c.date < m ? c.date : m, rows[0].date))
    };
  }).sort((a,b) => b.total - a.total);
}
function outstandingTable() {
  const rows = outstandingRows();
  if (!rows.length) return '<p class="empty">No outstanding balances.</p>';
  return `<div class="tbl-wrap"><table>
    <tr><th>Account</th><th>Balance due</th><th>Oldest charge</th><th>Aging</th><th></th></tr>
    ${rows.map(r => `<tr>
      <td><button class="tbl-link" onclick="viewParent('${r.acctId}')">${esc(r.account)}</button></td>
      <td class="amt" style="color:var(--red)">${fmt$(r.total)}</td>
      <td style="white-space:nowrap">${fmtD(r.oldest)}</td>
      <td><span class="pill ${r.bucket === 'Current' ? 'pill-off' : 'pill-due'}">${r.bucket}</span></td>
      <td style="white-space:nowrap;text-align:right">
        <button class="btn-ghost btn-sm" data-action="charge-card-confirm" data-account="${r.acctId}" data-name="${esc(r.account)}" data-total="${r.total}">Charge card</button>
        <button class="btn-ghost btn-sm" data-action="card-modal-for-user" data-account="${r.acctId}" style="margin-left:4px">${r.hasCard ? 'Update card' : 'Add card'}</button>
      </td></tr>`).join('')}
  </table></div>`;
}

function historyRows() {
  const from = getVal('phFrom', `${TODAY.getFullYear()}-01-01`), to = getVal('phTo', `${TODAY.getFullYear()}-12-31`), st = getVal('phStatus', '');
  return DB.charges.filter(c => c.date >= from && c.date <= to && (!st || c.status === st))
    .slice().sort((a,b) => b.date.localeCompare(a.date));
}
function historyTable() {
  const rows = historyRows();
  if (!rows.length) return '<p class="empty">No transactions match these filters.</p>';
  // Group by account + date so a multi-performer single transaction shows as one invoice
  const groups = {};
  rows.forEach(c => { const key = c.accountId + '|' + c.date; (groups[key] = groups[key] || []).push(c); });
  const invoices = Object.entries(groups).sort((a,b) => {
    const da = a[0].split('|')[1], db = b[0].split('|')[1];
    return db.localeCompare(da);
  });
  return `<div class="tbl-wrap"><table>
    <tr><th>Date</th><th>Account</th><th>Description</th><th>Amount</th><th>Status</th></tr>
    ${invoices.map(([key, irows]) => {
      const acctId = key.split('|')[0];
      const date = key.split('|')[1];
      const total = irows.reduce((s,c) => s + c.amount, 0);
      const allSame = irows.every(r => r.status === irows[0].status);
      const status = allSame ? irows[0].status : 'mixed';
      const single = irows.length === 1;
      const hasDue = irows.some(c => c.status === 'due');
      return `<tr>
        <td style="white-space:nowrap;vertical-align:top">${fmtD(date)}</td>
        <td style="vertical-align:top"><button class="tbl-link" onclick="viewParent('${acctId}')">${esc(acctName(acctId))}</button></td>
        <td>${irows.map(c => `<div style="padding:1px 0">${esc(c.desc)}<span class="sub" style="margin-left:6px">${esc(childName(c.childId))}</span>${!single ? `<span style="color:var(--gold-light);font-size:12px;margin-left:8px">${fmt$(c.amount)}</span>` : ''}</div>`).join('')}</td>
        <td class="amt" style="vertical-align:top${hasDue ? ';color:var(--red)' : ''}">${fmt$(total)}</td>
        <td style="vertical-align:top"><span class="pill ${status === 'mixed' ? 'pill-off' : 'pill-' + status}">${status === 'mixed' ? 'partial' : status}</span></td>
      </tr>`;
    }).join('')}
  </table></div>`;
}
function exportHistory() { exportCSV('payment-history.csv', ['Date','Account','Child','Description','Type','Amount','Status'], historyRows().map(c => [c.date, acctName(c.accountId), childName(c.childId), c.desc, c.type, c.amount, c.status])); }
